import {
	Checkbox,
	Form,
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import {
	AddTilesResponseModel,
	CalendarIntegrationTileLayoutModel,
	IntegrationAppDetailsTileModel,
	IntegrationTileCalendarViewKind,
	IntegrationTileFlowSettingsModel,
	IntegrationTileLayoutKind,
	IntegrationTileLayoutModel,
	IntegrationUpdateTileModel,
	updateIntegrationTile
} from "client/Api/Workspace/workspace";
import { IntegrationFlowKind } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { never } from "client_legacy/ndaw/errors";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ExistingTile } from "./common";
import { EditTileFromData, initialGenericState } from "./EditGenericTile";
import { EditTileHandle } from "./EditTile";
import { EditTileGeneralFields } from "./GeneralFields";
import { useUpdateTile, UpdateTileFormData } from "./hooks/useUpdateTile";
import { isNotNil } from "client/Misc/utils";

export type EditIntegrationTileProps = {
	initialModel: IntegrationAppDetailsTileModel;
	mode: ExistingTile;
	isLoading: boolean;
	onIsLoadingChange: (value: boolean) => void;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditIntegrationTile = forwardRef(
	(
		props: EditIntegrationTileProps,
		ref: Ref<EditTileHandle>
	): ReactElement => {
		const {
			initialModel,
			mode,
			isLoading,
			onIsLoadingChange,
			onAfterSave
		} = props;

		const [formData, setFormData] = useState<IntegrationTileEditFormData>({
			...initialGenericState(initialModel),
			hideHeader: initialModel.hideHeader,
			hideFooter: initialModel.hideFooter,
			layout: getInitialLayoutFormData(initialModel.flowSettings)
		});

		const formHandler: IFormHandler<IntegrationTileEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const shouldShowHideFooter: boolean = isNotNil(initialModel.navigationUrl);

		const { updateHandler } = useUpdateTile<IntegrationUpdateTileModel>(
			formHandler,
			updateIntegrationTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			(formData) => prepareForSave(formData, shouldShowHideFooter)
		);

		useImperativeHandle(
			ref,
			(): EditTileHandle => ({
				updateHandler
			}),
			[updateHandler]
		);

		return (
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>

				{formHandler.for("layout")
					.whenSet((layoutAttachable) =>
						layoutAttachable.whenType(
							isCalendarLayout,
							(calendarLayoutAttachable) => (
								<CalendarLayoutFormSection attachable={calendarLayoutAttachable} />
							)
						)
					)
				}

				<FormItem>
					<Checkbox
						{...formHandler.for("hideHeader").attachToCheckbox()}
						text={localize("_HideTileHeader")}
					/>
					<ValidationError
						{...formHandler.for("hideHeader").attachToValidationError()}
					/>
				</FormItem>
				{shouldShowHideFooter && (
					<FormItem>
						<Checkbox
							{...formHandler.for("hideFooter").attachToCheckbox()}
							text={localize("_HideTileFooter")}
						/>
						<ValidationError
							{...formHandler.for("hideFooter").attachToValidationError()}
						/>
					</FormItem>
				)}
			</Form>
		);
	}
);
EditIntegrationTile.displayName = "EditIntegrationTile";

type IntegrationTileEditFormData = EditTileFromData<
	OverrideProperties<
		IntegrationUpdateTileModel,
		{
			hideFooter: boolean;
		}
	>
>;

type CalendarLayoutFormSectionProps = {
	attachable: IValueInputAttachable<CalendarIntegrationTileLayoutModel>;
};

const CalendarLayoutFormSection = (props: CalendarLayoutFormSectionProps): ReactElement => {
	const { attachable } = props;

	return (
		<>
			<FormItem label={localize("_DefaultView")}>
				<Select
					{...attachable.for("defaultView").attachToSelect()}
					options={calendarViewOptions}
					menuTitle={localize("_DefaultView")}
					trackingId="default-view-select"
				/>
				<ValidationError {...attachable.for("defaultView").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_WeekView")}>
				<Checkbox
					{...attachable.for("showHoursColumn").attachToCheckbox()}
					text={localize("_ShowHoursColumn")}
				/>
				<ValidationError {...attachable.for("showHoursColumn").attachToValidationError()} />
			</FormItem>
		</>
	);
};
CalendarLayoutFormSection.displayName = "CalendarLayoutFormSection";

const calendarViewOptions: readonly SelectItem<IntegrationTileCalendarViewKind>[] = [
	{
		text: localize("_Week"),
		value: IntegrationTileCalendarViewKind.Week
	},
	{
		text: localize("_Day"),
		value: IntegrationTileCalendarViewKind.Day
	}
];

const defaultCalendarLayout: CalendarIntegrationTileLayoutModel = {
	kind: IntegrationTileLayoutKind.Calendar,
	defaultView: IntegrationTileCalendarViewKind.Week,
	showHoursColumn: true
};

function isCalendarLayout(
	layout: IntegrationTileLayoutModel | null
): layout is CalendarIntegrationTileLayoutModel {
	return layout?.kind === IntegrationTileLayoutKind.Calendar;
}

function getInitialLayoutFormData(
	flowSettings: IntegrationTileFlowSettingsModel
): IntegrationTileLayoutModel | null {
	switch (flowSettings.kind) {
		case IntegrationFlowKind.General:
			return null;
		case IntegrationFlowKind.Planning:
			return isCalendarLayout(flowSettings.layout)
				? flowSettings.layout
				: defaultCalendarLayout;
		default:
			never(flowSettings);
	}
}

function prepareForSave(
	formData: UpdateTileFormData<IntegrationUpdateTileModel>,
	shouldIncludeHideFooterSetting: boolean
): UpdateTileFormData<IntegrationUpdateTileModel> {
	return {
		...formData,
		hideFooter: shouldIncludeHideFooterSetting
			? formData.hideFooter
			: null
	};
}

const validationBuilder: IValidationConfiguration<IntegrationTileEditFormData> = createValidationConfiguration<IntegrationTileEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationBuilder.forObject("layout")
	.whenType(isCalendarLayout)
	.forScalar("defaultView")
	.add(
		isNotNullNorUndefined()
	);
